import { dequal } from 'dequal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Checkbox } from 'semantic-ui-react';
import { Input, Popup } from '../../lib/custom-ui';

import { useForm, useSteps } from '../../hooks';
import DeleteStep from '../DeleteStep';

import styles from './EditStep.module.scss';

const StepTypes = {
  DELETE: 'DELETE',
};

const EditStep = React.memo(({ defaultData, onUpdate, onDelete, onClose }) => {
  const [t] = useTranslation();

  const [checkboxEnabled, setCheckBoxEnabled] = useState(null); // eslint-disable-line no-unused-vars

  const [data, handleFieldChange] = useForm(() => ({
    gitProject: '',
    name: '',
    ...defaultData,
  }));

  const [step, openStep, handleBack] = useSteps();

  const nameField = useRef(null);
  const gitProjectField = useRef(null);

  const handleSubmit = useCallback(() => {
    const cleanData = {
      ...data,
      name: data.name.trim(),
      gitProject: (data.gitProject ?? '').trim(),
    };

    if (!cleanData.name) {
      nameField.current.select();
      return;
    }

    if (!dequal(cleanData, defaultData)) {
      onUpdate(cleanData);
    }

    onClose();
  }, [defaultData, onUpdate, onClose, data]);

  const handleDeleteClick = useCallback(() => {
    openStep(StepTypes.DELETE);
  }, [openStep]);

  useEffect(() => {
    nameField.current.select();
  }, []);

  if (step && step.type === StepTypes.DELETE) {
    return (
      <DeleteStep
        title="common.deleteBoard"
        content="common.areYouSureYouWantToDeleteThisBoard"
        buttonContent="action.deleteBoard"
        onConfirm={onDelete}
        onBack={handleBack}
      />
    );
  }

  return (
    <>
      <Popup.Header>
        {t('common.editBoard', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <Form onSubmit={handleSubmit}>
          <div className={styles.text}>{t('common.title')}</div>
          <Input
            fluid
            ref={nameField}
            name="name"
            value={data.name}
            className={styles.field}
            onChange={handleFieldChange}
          />
          <div className={styles.text}>{t('common.developmentBoard')}</div>
          {/* TODO: debug test - to delete! */}
          {/* <ul>
            <li>devBoard (string): {String(data.devBoard)}</li>
            <li>gitProject: {data.gitProject ?? '-'}</li>
          </ul> */}
          {/* TODO: debug test - to delete! */}
          <Checkbox
            checked={data.devBoard ?? false}
            toggle
            name="devBoard"
            className={styles.fieldCheckbox}
            onChange={(event, dt) => {
              setCheckBoxEnabled(dt.checked);
              data.devBoard = !data.devBoard;
            }}
          />
          {data.devBoard && (
            <div>
              <div className={styles.text}>{t('common.gitProject')}</div>
              <Input
                fluid
                ref={gitProjectField}
                name="gitProject"
                value={data.gitProject ?? ''}
                className={styles.field}
                onChange={handleFieldChange}
              />
            </div>
          )}
          <Button positive content={t('action.save')} />
        </Form>
        <Button
          content={t('action.delete')}
          className={styles.deleteButton}
          onClick={handleDeleteClick}
        />
      </Popup.Content>
    </>
  );
});

EditStep.propTypes = {
  defaultData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditStep;
